import React, { useState, useEffect, useRef } from 'react';
import {
  LinkIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  ClipboardIcon,
  CheckIcon,
  LockClosedIcon,
  CalendarIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';

export default function ShareLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  useEffect(() => {
    loadShareLinks();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadShareLinks = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch active share links via RPC function
      const { data, error: fetchError } = await supabase
        .rpc('get_org_active_share_links');

      if (fetchError) throw fetchError;

      // Transform the flat data into nested structure for compatibility
      const transformedLinks = (data || []).map(link => ({
        ...link,
        prototype_sessions: {
          id: link.session_id,
          name: link.session_name,
          project_id: link.project_id,
          projects: {
            id: link.project_id,
            name: link.project_name
          }
        },
        profiles: {
          full_name: link.creator_name,
          email: link.creator_email
        }
      }));

      setLinks(transformedLinks);
    } catch (err) {
      console.error('Error loading share links:', err);
      setError('Failed to load share links. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredLinks = links.filter(link => {
    const searchLower = searchTerm.toLowerCase();
    const sessionName = link.prototype_sessions?.name || '';
    const projectName = link.prototype_sessions?.projects?.name || '';
    const contactName = link.contact_name || '';
    const contactEmail = link.contact_email || '';
    
    return (
      sessionName.toLowerCase().includes(searchLower) ||
      projectName.toLowerCase().includes(searchLower) ||
      contactName.toLowerCase().includes(searchLower) ||
      contactEmail.toLowerCase().includes(searchLower) ||
      link.token.toLowerCase().includes(searchLower)
    );
  });

  const handleCopyLink = async (e, link) => {
    e.stopPropagation();
    const url = `${window.location.origin}/share/${link.token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleMenuClick = (e, linkId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === linkId ? null : linkId);
  };

  const handleEditClick = (link) => {
    setSelectedLink(link);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleDisableClick = (link) => {
    setSelectedLink(link);
    setShowDisableModal(true);
    setOpenMenuId(null);
  };

  const handleLinkUpdated = (updatedLink) => {
    setLinks(prev => prev.map(link =>
      link.id === updatedLink.id ? { ...link, ...updatedLink } : link
    ));
    setShowEditModal(false);
    setSelectedLink(null);
  };

  const handleDisableLink = async () => {
    if (!selectedLink) return;

    try {
      const { error: updateError } = await supabase
        .from('shared_links')
        .update({ is_active: false })
        .eq('id', selectedLink.id);

      if (updateError) throw updateError;

      // Remove from list since we only show active links
      setLinks(prev => prev.filter(link => link.id !== selectedLink.id));
      setShowDisableModal(false);
      setSelectedLink(null);
    } catch (err) {
      console.error('Error disabling link:', err);
      setError('Failed to disable link: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  const getExpirationStatus = (link) => {
    if (!link.expires_at) return { text: 'Never', status: 'normal' };
    
    const expiresAt = new Date(link.expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry <= 0) return { text: 'Expired', status: 'expired' };
    if (daysUntilExpiry <= 7) return { text: `${daysUntilExpiry}d left`, status: 'warning' };
    return { text: formatDate(link.expires_at), status: 'normal' };
  };

  const LinkActionMenu = ({ link, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div
        ref={menuRef}
        className="absolute right-0 top-8 w-48 bg-white rounded-sm shadow-lg border border-gray-200 py-1 z-50"
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick(link);
          }}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
        >
          <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
          Edit Link
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDisableClick(link);
          }}
          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
        >
          <NoSymbolIcon className="h-4 w-4 mr-3 text-red-400" />
          Disable Link
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header - aligned with Layout sidebar h-14 */}
      <div className="flex-shrink-0 h-14 bg-white border-b border-gray-200 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Share Links</h1>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-xs text-gray-400">Create links from session Share tab</p>
          <div className="relative w-64">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search links..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-1.5 w-full text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto">

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3">
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Links Table */}
        {links.length > 0 && (
          <div className="bg-white border-b border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLinks.map((link) => {
                  const expiration = getExpirationStatus(link);
                  
                  return (
                    <tr
                      key={link.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex items-center justify-center w-10 h-10 rounded-sm mr-3 flex-shrink-0 ${link.password_hash ? 'bg-amber-50' : 'bg-gray-100'}`} title={link.password_hash ? 'Password protected' : 'No password'}>
                            {link.password_hash ? (
                              <LockClosedIcon className="h-5 w-5 text-amber-600" />
                            ) : (
                              <LinkIcon className="h-5 w-5 text-gray-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {link.prototype_sessions?.name || 'Unknown Session'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {link.prototype_sessions?.projects?.name || 'Unknown Project'}
                            </div>
                            {(link.contact_name || link.contact_email) && (
                              <div className="text-xs text-gray-400 truncate mt-0.5">
                                {link.contact_name}{link.contact_name && link.contact_email && ' · '}{link.contact_email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center text-sm ${
                          expiration.status === 'expired' ? 'text-red-600' :
                          expiration.status === 'warning' ? 'text-amber-600' :
                          'text-gray-900'
                        }`}>
                          <CalendarIcon className={`h-4 w-4 mr-1.5 ${
                            expiration.status === 'expired' ? 'text-red-400' :
                            expiration.status === 'warning' ? 'text-amber-400' :
                            'text-gray-400'
                          }`} />
                          {expiration.text}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <EyeIcon className="h-4 w-4 text-gray-400 mr-1.5" />
                          {link.view_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div title={formatDate(link.created_at)}>
                          {getRelativeDate(link.created_at)}
                        </div>
                        {link.profiles && (
                          <div className="text-xs text-gray-400 truncate max-w-[120px]">
                            by {link.profiles.full_name || link.profiles.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => handleCopyLink(e, link)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
                            title="Copy link"
                          >
                            {copiedId === link.id ? (
                              <CheckIcon className="h-4 w-4 text-green-600" />
                            ) : (
                              <ClipboardIcon className="h-4 w-4" />
                            )}
                          </button>
                          <a
                            href={`${window.location.origin}/share/${link.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
                            title="Open link"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </a>
                          <button
                            onClick={(e) => handleMenuClick(e, link.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors relative"
                          >
                            <EllipsisVerticalIcon className="h-4 w-4" />
                            <LinkActionMenu
                              link={link}
                              isOpen={openMenuId === link.id}
                              onClose={() => setOpenMenuId(null)}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty States */}
        {!loading && links.length === 0 && (
          <div className="bg-white h-full flex items-center justify-center">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                <LinkIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No Active Share Links</h3>
              <p className="mt-1 text-sm text-gray-500 max-w-sm">
                There are no active share links. Create links from the session Share tab.
              </p>
            </div>
          </div>
        )}

        {!loading && links.length > 0 && filteredLinks.length === 0 && (
          <div className="bg-white h-full flex items-center justify-center">
            <div className="text-center">
              <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-gray-400" />
              <h3 className="mt-3 text-lg font-semibold text-gray-900">No Links Found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No results for "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
              >
                Clear Search
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedLink && (
        <EditLinkModal
          link={selectedLink}
          onClose={() => {
            setShowEditModal(false);
            setSelectedLink(null);
          }}
          onUpdated={handleLinkUpdated}
        />
      )}

      {/* Disable Confirmation Modal */}
      {showDisableModal && selectedLink && (
        <DisableModal
          link={selectedLink}
          onClose={() => {
            setShowDisableModal(false);
            setSelectedLink(null);
          }}
          onConfirm={handleDisableLink}
        />
      )}
    </div>
  );
}

// Edit Link Modal
const EditLinkModal = ({ link, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    password: '',
    usePassword: !!link.password_hash,
    changePassword: false,
    neverExpires: !link.expires_at,
    customExpiry: link.expires_at ? new Date(link.expires_at).toISOString().split('T')[0] : '',
    contactName: link.contact_name || '',
    contactEmail: link.contact_email || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const updates = {
        contact_name: formData.contactName || null,
        contact_email: formData.contactEmail || null,
        expires_at: formData.neverExpires
          ? null
          : formData.customExpiry
            ? new Date(formData.customExpiry).toISOString()
            : null,
      };

      if (formData.changePassword) {
        if (formData.usePassword && formData.password) {
          updates.password_hash = await hashPassword(formData.password);
        } else if (!formData.usePassword) {
          updates.password_hash = null;
        }
      }

      const { data, error: updateError } = await supabase
        .from('shared_links')
        .update(updates)
        .eq('id', link.id)
        .select()
        .single();

      if (updateError) throw updateError;
      onUpdated(data);
    } catch (err) {
      console.error('Error updating link:', err);
      setError('Failed to update link: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Edit Share Link</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* Session Info */}
          <div className="bg-gray-50 rounded-sm p-3 border border-gray-200">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Session</div>
            <div className="text-sm font-medium text-gray-900">
              {link.prototype_sessions?.name || 'Unknown Session'}
            </div>
            <div className="text-xs text-gray-500">
              {link.prototype_sessions?.projects?.name || 'Unknown Project'}
            </div>
          </div>

          {/* Password Protection */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.usePassword}
                onChange={(e) => setFormData({
                  ...formData,
                  usePassword: e.target.checked,
                  changePassword: true
                })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">Password protect this link</span>
            </label>
            {formData.usePassword && (
              <div className="mt-2 space-y-2">
                {link.password_hash && !formData.changePassword ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Current password is set</span>
                    <button
                      onClick={() => setFormData({ ...formData, changePassword: true })}
                      className="text-red-600 hover:underline"
                    >
                      Change password
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value, changePassword: true })}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                )}
              </div>
            )}
          </div>

          {/* Expiration */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Expiration</div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.neverExpires}
                onChange={(e) => setFormData({ ...formData, neverExpires: e.target.checked })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Never expires</span>
            </label>
            {!formData.neverExpires && (
              <input
                type="date"
                value={formData.customExpiry}
                onChange={(e) => setFormData({ ...formData, customExpiry: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Contact Info</div>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Contact name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              placeholder="Contact email"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3">
              <div className="flex">
                <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (formData.usePassword && formData.changePassword && !formData.password)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Disable Confirmation Modal
const DisableModal = ({ link, onClose, onConfirm }) => {
  const [disabling, setDisabling] = useState(false);

  const handleConfirm = async () => {
    setDisabling(true);
    await onConfirm();
    setDisabling(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Disable Link</h3>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to disable this share link for <span className="font-medium">{link.prototype_sessions?.name || 'this session'}</span>?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Anyone with this link will no longer be able to access the session. This action cannot be undone.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={disabling}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {disabling ? 'Disabling...' : 'Disable Link'}
          </button>
        </div>
      </div>
    </div>
  );
};